import React, { useRef, useState, useEffect } from 'react';
import { DebounceInput } from 'react-debounce-input';
import { FOLDER_USER_ROLE } from '../../../constants/UserRoles';
import { useUserContext } from '../../../context/UserContext';
import { CustomButton } from '../../Button/Button';
import Select from '../Select/Select';
import UserItem from '../UserItem/UserItem';
import { useTranslation } from 'react-i18next';
import styles from './ShareModalContent.module.scss';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import { Icon } from '../../Icon';
import { ToastNotification } from '../../Toast/Toast';
import { isEmailFormatValid } from '../../../utils/stringHelper';

const ShareModalContent = ({
  isRedirect,
  contentType,
  setContentType,
  folder,
  onClose,
  sharedFolderWithUsers,
  editFolderUsers,
  deleteFolderUsers,
  changeFolderEditingRight,
  newUsers,
  setNewUsers,
  unvalidEmails,
  setUnvalidEmails,
}) => {
  const inputRef = useRef(null);

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const [newUserInfo, setNewUserInfo] = useState({ email: '', folderRole: FOLDER_USER_ROLE.EDITOR.TYPE });
  const [isInputFocus, setIsInputFocus] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [existingUsers, setExistingUsers] = useState([]);
  const [isFolderEditable, setIsFolderEditable] = useState(!!folder?.editingEnabled);
  const [additionalMessage, setAdditionalMessage] = useState({ active: false, text: '' });

  const [editedExistingUsers, setEditedExistingUsers] = useState([]);
  const [deletedExistingUsers, setDeletedExistingUsers] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const { user: currentUser } = useUserContext();
  const [isFolderOwner] = useState(folder?.owner?.username === currentUser.username);
  const history = useHistory();
  const { t } = useTranslation();

  // change email in input field and check for validity
  const handleChangeEmail = event => {
    const changeInput = event.target.value.trim();
    if (isEmailFormatValid(changeInput) && checkEmail(changeInput)) {
      setNewUserInfo({ ...newUserInfo, email: changeInput });
      setIsEmailValid(true);
      return;
    }
    setIsEmailValid(false);
  };

  function extractEmails(text) {
    return text.match(/([a-zA-Z0-9._-]+[\+[a-zA-Z0-9._-]*@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi);
  }

  const checkEmailFormat = email => {
    const re = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/gm;
    return re.test(email.toLowerCase());
  };

  const handlePaste = e => {
    e.preventDefault();
    const oldValue = e.target.value;
    const value = oldValue + ' ' + (e.clipboardData?.getData('Text') ?? '');
    const extractedEmails = extractEmails(value);

    var validEmails = [];
    var notValidEmails = [];

    if (extractedEmails?.length > 0) {
      extractedEmails.forEach(item => {
        const isMatchRegEx = checkEmailFormat(item);
        const duplicat = validEmails.find(({ email }) => email === item) || notValidEmails.find(({ email }) => email === item);
        if (!duplicat) {
          if (checkEmail(item) && isMatchRegEx && !duplicat) {
            validEmails.push({ email: item, folderRole: newUserInfo.folderRole });
          } else if (!isMatchRegEx) {
            notValidEmails.push(item);
          }
        }
      });
      setNewUsers([...newUsers, ...validEmails]);
    }
    setNewUserInfo({ email: '', folderRole: FOLDER_USER_ROLE.EDITOR.TYPE }); // reset value
    setUnvalidEmails(notValidEmails);
    setIsEmailValid(false);
    e.preventDefault();
  };

  const checkEmail = item => {
    const isEmailAlreadyExist = existingUsers.find(({ email }) => email === item);
    const isEmailAlreadyAdded = newUsers.find(({ email }) => email === item);
    const isEmailOwner = folder?.owner?.email === item;

    if (!isEmailAlreadyExist && !isEmailAlreadyAdded && !isEmailOwner) {
      return true;
    } else {
      return false;
    }
  };

  // add new user to draft list (not submit)
  const handleAddNewUserByEnter = event => {
    if (isEmailValid && event.key === 'Enter') {
      event.preventDefault();
      addNewUser();
    }
  };

  const onBlurInput = () => {
    // make a small delay so that the user has time to click
    // on the dropdown before the dropdown closes
    setTimeout(() => setIsInputFocus(false), 100);
  };

  // change role in input field (before handle adding to draft list)
  const handleChangeDefaultRole = ({ folderRole }) => {
    setNewUserInfo({ ...newUserInfo, folderRole });
  };

  const onFocusInput = () => {
    setIsInputFocus(true);
  };

  const addNewUser = async () => {
    try {
      handleNewUser(newUserInfo);
      setNewUserInfo({ email: '', folderRole: FOLDER_USER_ROLE.EDITOR.TYPE }); // reset value
      setIsEmailValid(false);
    } catch (e) {
      console.log('error CmsAPI.isEmailRecognized', e);
    }
  };

  useEffect(() => {
    getDefaultInfo();
  }, [folder]);

  useEffect(() => {
    if (newUsers.length) {
      setContentType(2);
      return;
    }

    setContentType(1);
  }, [newUsers]);

  useEffect(() => {
    // track editing of existing users
    const editedUsers = existingUsers.filter(existingUser => {
      const editedUser = folder?.users?.find(user => user.username === existingUser.username);
      if (editedUser) {
        return existingUser.folderRole !== editedUser.folderRole;
      }

      return false;
    });

    // track deletion of existing users
    const existingUsersIds = existingUsers.map(({ username }) => username);
    const deletedUsers = folder?.users?.filter(user => !existingUsersIds.includes(user.username)) || [];

    setEditedExistingUsers(editedUsers);
    setDeletedExistingUsers(deletedUsers);
  }, [existingUsers]);

  const getDefaultInfo = () => {
    setUnvalidEmails([]);
    setExistingUsers(folder?.users || []);
    setIsFolderEditable(!!folder?.editingEnabled);
  };

  const handleShare = async () => {
    setShareLoading(true);
    // if a user edited or deleted existing users and immediately shared a folder with a new user,
    // the changes for existing users will be saved
    try {
      if (editedExistingUsers.length) {
        await editFolderUsers(folder.id, editedExistingUsers);
      }

      if (deletedExistingUsers.length) {
        await deleteFolderUsers(folder.id, deletedExistingUsers);
      }

      if (isFolderEditable !== !!folder?.editingEnabled) {
        await changeFolderEditingRight(folder.id, isFolderEditable);
      }

      if (editedExistingUsers.length || deletedExistingUsers.length || isFolderEditable !== !!folder?.editingEnabled) {
        ToastNotification({
          message: `${t('general.toast.update_successfully_folders')}`,
          iconName: 'shareFolder',
          isMobileView: isMobileView,
        });
      }

      // Toast to sharedFolderWithUsers will be displayed
      // in useFetcFolderList after receiving a response from the backend
      // that the user is not outside the company

      if (newUsers.length > 0) {
        await sharedFolderWithUsers(
          folder.id,
          {
            users: newUsers,
            ...(additionalMessage.active && additionalMessage.text && { message: additionalMessage.text.trim() }),
          },
          isRedirect
        );
      }

      setShareLoading(false);
    } catch (e) {
      console.log(e);
      onClose();
    }
  };

  const handleSave = async () => {
    setShareLoading(true);
    try {
      if (editedExistingUsers.length) {
        await editFolderUsers(folder.id, editedExistingUsers);
      }

      if (deletedExistingUsers.length) {
        await deleteFolderUsers(folder.id, deletedExistingUsers);
      }

      if (isFolderEditable !== !!folder?.editingEnabled) {
        await changeFolderEditingRight(folder.id, isFolderEditable);
      }

      if (editedExistingUsers.length || deletedExistingUsers.length || isFolderEditable !== !!folder?.editingEnabled) {
        ToastNotification({
          message: `${t('general.toast.update_successfully_folders')}`,
          iconName: 'shareFolder',
          isMobileView: isMobileView,
        });
      }

      if (newUserInfo.email.length !== 0) {
        await sharedFolderWithUsers(
          folder.id,
          {
            users: [newUserInfo],
            ...(additionalMessage.active && additionalMessage.text && { message: additionalMessage.text.trim() }),
          },
          isRedirect
        );
      }

      if (isRedirect && deletedExistingUsers.length === folder?.users?.length) {
        history.push(`/eg/dashboard`, { folders: true });
      }

      onClose();
    } catch (e) {
      console.log(e);
    } finally {
      setShareLoading(false);
    }
  };

  const handleToggleFolderEditable = () => {
    setIsFolderEditable(!isFolderEditable);
  };

  const handleToggleMessage = () => {
    setAdditionalMessage({ active: !additionalMessage.active, text: additionalMessage.text });
  };

  const handleChangeExistingUserRole = ({ email, folderRole }) => {
    setExistingUsers(prev =>
      prev.map(existingUser => {
        if (existingUser.email === email) {
          return { ...existingUser, folderRole };
        }

        return existingUser;
      })
    );
  };

  const handleChangeNewUserRole = ({ email, folderRole }) => {
    setNewUsers(prev =>
      prev.map(newUser => {
        if (newUser.email === email) {
          return { email, folderRole };
        }

        return newUser;
      })
    );
  };

  const handleDeleteExistingUser = username => {
    setExistingUsers(prev => prev.filter(existingUser => existingUser.username !== username));
  };

  const handleDeleteNewUser = email => {
    setNewUsers(prev => prev.filter(newUser => newUser.email !== email));
  };

  const handleNewUser = user => {
    setNewUsers([...newUsers, { email: user.email, folderRole: user.folderRole }]);
  };

  return (
    <>
      <span className={styles.inputTitle}>{t('share_folder_modal.first_step.add_people')}</span>
      <div className={styles.inputContainer}>
        <DebounceInput
          inputRef={inputRef}
          type="text"
          className={styles.inputField}
          placeholder={t('general.email')}
          debounceTimeout={300}
          value={newUserInfo.email}
          onChange={handleChangeEmail}
          onKeyDown={handleAddNewUserByEnter}
          onBlur={onBlurInput}
          onFocus={onFocusInput}
          onPaste={handlePaste}
        />
        <Select currentUser={newUserInfo} data={FOLDER_USER_ROLE} onClick={handleChangeDefaultRole} />
        {isEmailValid && isInputFocus && (
          <div className={styles.inputDropdown} onMouseDown={addNewUser}>
            <div className={styles.photoPlaceholder}>{newUserInfo.email[0]?.toUpperCase()}</div>
            <span className={styles.email}>{newUserInfo.email}</span>
          </div>
        )}
      </div>
      {unvalidEmails.length > 0 && (
        <div className={styles.unvalidEmailsContainer}>
          <div className={styles.header}>
            <p className={styles.unvalidEmails}>{t('share_folder_modal.emails_not_accepted')} </p>
            <Icon src="close" className={styles.deleteIcon} onClick={() => setUnvalidEmails([])} />
          </div>
          <ul className={styles.unvalidEmailList}>
            {unvalidEmails.map(item => (
              <li key={item} className={styles.unvalidEmail}>
                {' '}
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {contentType === 1 && (
        <div className={styles.ownerContainer}>
          <div className={styles.ownerInfo}>
            <div className={styles.photoPlaceholder}>
              {!folder?.owner?.avatar && folder?.owner?.name[0]?.toUpperCase()}
              {folder?.owner?.avatar && <img src={folder.owner.avatar} className={styles.avatar} alt={t('clip_organizer.add_modal.avatar')} />}
            </div>
            <span className={styles.email}>{folder?.owner?.email}</span>
            {isFolderOwner && <span>({t('share_folder_modal.first_step.you')})</span>}
          </div>
          <span className={styles.ownerText}>{t('folder.role.owner')}</span>
        </div>
      )}
      {/*List of existings users. If there are more than 6 users, then add style for container height
        I didn't set the default style, because then the role selection window is not displayed as expected*/}
      {contentType === 1 && (
        <div className={cx(styles.usersContainer, { [styles.usersContainerCrowded]: existingUsers.length > 6 })}>
          {existingUsers.map((existingUser, index) => (
            <UserItem
              key={index}
              user={existingUser}
              existingUser={true}
              newUser={false}
              currentUser={currentUser}
              index={index}
              isMobileView={isMobileView}
              handleDelete={handleDeleteExistingUser}
            >
              <Select currentUser={existingUser} data={FOLDER_USER_ROLE} onClick={handleChangeExistingUserRole} />
            </UserItem>
          ))}
        </div>
      )}
      {/*List of new users. If there are more than 6 users, then add style for container height
        I didn't set the default style, because then the role selection window is not displayed as expected*/}
      {contentType === 2 && (
        <div className={cx(styles.usersContainer, { [styles.usersContainerCrowded]: newUsers.length > 6 })}>
          {newUsers.map((newUser, index) => (
            <UserItem
              key={index}
              user={newUser}
              existingUser={false}
              newUser={true}
              index={index}
              isMobileView={isMobileView}
              handleDelete={handleDeleteNewUser}
            >
              <Select currentUser={newUser} data={FOLDER_USER_ROLE} onClick={handleChangeNewUserRole} />
            </UserItem>
          ))}
        </div>
      )}
      {contentType === 1 && isFolderOwner && (
        <>
          <div className={styles.line} />
          <div className={styles.checkboxContainer}>
            <div onClick={handleToggleFolderEditable} className={cx(styles.checkbox, { [styles.checkboxActive]: isFolderEditable })}>
              {isFolderEditable && <Icon src="vector" />}
            </div>
            <span className={styles.checkboxText}>{t('share_folder_modal.first_step.editors_checkbox')}</span>
          </div>
        </>
      )}
      {contentType === 2 && (
        <>
          <div className={styles.line} />
          <div className={styles.checkboxContainer}>
            <div onClick={handleToggleMessage} className={cx(styles.checkbox, { [styles.checkboxActive]: additionalMessage.active })}>
              {additionalMessage.active && <Icon src="vector" />}
            </div>
            <span className={styles.checkboxText}>{t('share_folder_modal.second_step.add_message')}</span>
          </div>
        </>
      )}
      {contentType === 2 && additionalMessage.active && (
        <textarea
          className={styles.messageTextArea}
          onChange={event => setAdditionalMessage({ active: additionalMessage.active, text: event.currentTarget.value })}
          rows="10"
          cols="45"
          name="text"
          placeholder={t('share_folder_modal.second_step.add_message.placeholder')}
          value={additionalMessage.text}
        />
      )}
      {contentType === 1 && (
        <div className={styles.buttonsContainer}>
          {/*This Cancel button refresh all actions the user has done to existing users*/}
          {!!(editedExistingUsers.length || deletedExistingUsers.length || isFolderEditable !== !!folder?.editingEnabled) && (
            <CustomButton className={styles.btn} variant="secondary" label={t('general.cancel')} onClick={getDefaultInfo} />
          )}
          <CustomButton
            className={styles.saveBtn}
            variant="primary"
            label={
              editedExistingUsers.length || deletedExistingUsers.length || isFolderEditable !== !!folder?.editingEnabled
                ? t('general.save')
                : t('general.done')
            }
            onClick={handleSave}
            loading={shareLoading}
            loadingText={t('general.saving')}
          />
        </div>
      )}
      {contentType === 2 && (
        <div className={styles.buttonsContainer}>
          {/*This Cancel button refresh all newly added users*/}
          <CustomButton
            variant="secondary"
            label={t('general.cancel')}
            onClick={() => {
              setNewUsers([]);
              setUnvalidEmails([]);
            }}
          />
          <CustomButton
            className={styles.saveBtn}
            variant="primary"
            label={t('general.share')}
            onClick={handleShare}
            loading={shareLoading}
            loadingText={t('general.saving')}
          />
        </div>
      )}
    </>
  );
};

export default ShareModalContent;
